import type { CoPubKeyRecord, IShare } from '@wovin/core/pubsub'
import type { AgentStateClass } from './AgentState'

import { aesUtils, deriveSharedEncryptionKey, SymmAlg } from '@wovin/crypto'
import { arrayBufferToBase64, base64ToArrayBuffer } from '@wovin/utils'
import { Logger } from 'besonders-logger'
import { importPublicDerivationKey } from '../../ipfs/share-sync'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.INFO) // eslint-disable-line unused-imports/no-unused-vars

/**
 * Whether `ag` is registered as a co-publisher on `share` (i.e. listed in
 * `share.coPublishers`). On the pull side the authoritative check is the
 * presence of `ag` in the `share/copubkey` record's `encKeyMap` (the originator
 * encrypts the IPNS private key exactly for the co-publishers), but once a
 * share has been recovered/registered locally this reflects that state.
 */
export function isCoPublisher(share: IShare | undefined | null, ag: string): boolean {
	return !!share?.coPublishers?.includes(ag)
}

/**
 * Recover the co-publish IPNS private key (`share.pk` bytes) from a
 * `share/copubkey` record that the originator wrote into the share snapshot.
 *
 * The originator (tracked via `originatorAg`, taken from the `share/copubkey`
 * applog's `ag` field — the `CoPubKeyRecord` itself carries no `ag`) encrypted a
 * one-shot AES-GCM key per co-publisher via ECDH(originatorPriv, coPublisherPub)
 * and stored it in `encKeyMap[coPublisherAg]`. It then encrypted `share.pk`
 * with that one-shot AES key into `encPk` (with the IV in `iv`).
 *
 * Recovery:
 *  1. ECDH-derive the shared key from our own private derivation key + the
 *     originator's public derivation key (from `knownAgentsMap[originatorAg].jwkd`).
 *  2. Decrypt our `encKeyMap[myAg]` entry (IV-prefixed ciphertext, same format
 *     as `sharedKeyMap`) to get the raw one-shot AES key.
 *  3. Import that raw key and decrypt `encPk` (explicit IV) to recover `pk`.
 *
 * Returns the protobuf-encoded Ed25519 private key bytes suitable for
 * `W3Name.from(pk)` — which the caller verifies matches `share.id`.
 *
 * Throws if this agent is not in `encKeyMap` or the originator is unknown.
 */
export async function recoverCoPublishKey(
	record: CoPubKeyRecord,
	agent: AgentStateClass,
	originatorAg: string,
): Promise<Uint8Array> {
	const myAg = agent.ag
	const encAesKeyB64 = record.encKeyMap[myAg]
	if (!encAesKeyB64) {
		throw ERROR(`[recoverCoPublishKey] agent ${myAg} is not in encKeyMap (not a co-publisher)`, { myAg, originatorAg, keys: Object.keys(record.encKeyMap) })
	}

	const { privateKey: ownPriv } = await agent.getOrSetDerivationKeypair()

	const knownAgentsMap = agent.getKnownAgents().get()
	const originatorEntry = knownAgentsMap.get(originatorAg)
	const originatorJwkd = originatorEntry?.jwkd as string | undefined
	if (!originatorJwkd) {
		throw ERROR(`[recoverCoPublishKey] originator ${originatorAg} has no jwkd in knownAgentsMap — add agent to roster / known agents first`, { originatorAg, knownAgentsMap })
	}

	const originatorPub = await importPublicDerivationKey(originatorJwkd)
	// `deriveSharedEncryptionKey` returns an AES-GCM CryptoKey derived via ECDH;
	// it is the decryption key for our `encKeyMap` entry (mirrors the
	// `sharedKeyMap` decryption path in `getAESkeyForEncryptedApplogs`).
	const derivedKey = await deriveSharedEncryptionKey(ownPriv, originatorPub, true)

	// encKeyMap[myAg] = base64(IV(16) || ciphertext) — `aesUtils.decrypt` strips
	// the 16-byte IV prefix and uses it. Returns the raw one-shot AES key bytes.
	const aesKeyRaw = await aesUtils.decrypt(base64ToArrayBuffer(encAesKeyB64), derivedKey, SymmAlg.AES_GCM)
	DEBUG('[recoverCoPublishKey] recovered one-shot AES key', { myAg, originatorAg })

	const aesKey = await globalThis.crypto.subtle.importKey(
		'raw',
		aesKeyRaw,
		{ name: 'AES-GCM', length: 256 },
		false,
		['decrypt'],
	)

	// encPk is the pure ciphertext (the originator split IV(16) || ct and stored
	// the IV separately in `record.iv`), so we decrypt with an explicit IV via
	// subtle.decrypt rather than the IV-prefixed `aesUtils.decrypt`.
	const ivBytes = base64ToArrayBuffer(record.iv)
	const pkBuffer = await globalThis.crypto.subtle.decrypt(
		{ name: 'AES-GCM', iv: ivBytes },
		aesKey,
		base64ToArrayBuffer(record.encPk),
	)
	const pkBytes = new Uint8Array(pkBuffer)
	VERBOSE('[recoverCoPublishKey] recovered pk bytes', { myAg, pkBytes: arrayBufferToBase64(pkBytes) })
	return pkBytes
}
